/**
 * Fait : le choix du RÉFÉRENT à la création d'un projet (`D-023`) — la liste
 *   des parents administrateurs qu'un membre peut désigner, et le niveau qu'il
 *   lui donne. Il n'apparaît que pour un membre : un parent administrateur n'a
 *   personne à désigner.
 *
 * POURQUOI CHAQUE ÉTAT DE LA LISTE EST DIT, et pas seulement le cas ordinaire.
 *   Un menu déroulant qui n'a qu'une entrée ressemble à un menu cassé, et un
 *   menu vide ressemble à une panne. Le critère 135 du chantier l'exige : quand
 *   il n'y a qu'un seul référent possible, l'écran le DIT au lieu de laisser
 *   croire que le reste n'a pas chargé.
 *
 * Ne fait pas : il ne décide pas si un référent est obligatoire — le serveur le
 *   sait, parce qu'il connaît le rôle du demandeur, et son refus nomme le choix
 *   attendu. L'écran pose le champ ; il ne réécrit pas la règle.
 */

import type { NiveauDeMembre } from '@aymoai/shared';

import { Annonce } from '../gabarits/Annonce.tsx';
import type { ReferentPossibleVu } from './api.ts';

/** Ce que chaque niveau donne au référent, dit en français. */
const CE_QUE_LE_NIVEAU_DONNE: Readonly<Record<NiveauDeMembre, string>> = {
  lecture: 'Il pourra lire ce projet',
  modification: 'Il pourra lire et écrire dans ce projet',
};

export function ChoixDuReferent({
  candidats,
  choisi,
  surChoix,
  niveau,
  surNiveau,
}: {
  /** `null` tant que la liste n'a pas été chargée. */
  readonly candidats: ReferentPossibleVu[] | null;

  readonly choisi: string;
  readonly surChoix: (compteId: string) => void;
  readonly niveau: NiveauDeMembre;
  readonly surNiveau: (niveau: NiveauDeMembre) => void;
}) {
  if (candidats === null) {
    return (
      <p data-role="referents-en-chargement" className="texte-attenue" aria-busy="true">
        Chargement des personnes que vous pouvez choisir comme référent…
      </p>
    );
  }

  /*
   * AUCUN CANDIDAT. Ce cas ne devrait jamais arriver — la famille a toujours un
   * fondateur, et il ne peut pas être fermé. Mais un écran qui n'aurait rien à
   * dire ici afficherait une liste vide et un bouton qui refuse, sans que
   * personne comprenne pourquoi.
   */
  if (candidats.length === 0) {
    return (
      <span data-role="aucun-referent">
        <Annonce urgence="urgent">
          Aucun parent administrateur ne peut suivre votre projet pour l’instant. Prévenez la
          personne qui administre la famille : sans référent, le projet ne peut pas être créé.
        </Annonce>
      </span>
    );
  }

  const seulCandidat = candidats.length === 1 ? candidats[0] : undefined;

  return (
    <>
      <p className="champ" data-role="choix-du-referent">
        <label htmlFor="referent-du-projet">Le référent de ce projet</label>

        <span id="aide-du-referent" className="texte-attenue">
          {seulCandidat === undefined
            ? 'La personne vers qui vous vous tournerez pour ce projet. Elle le verra.'
            : /*
               * CRITÈRE 135 : un seul candidat se DIT. Sans cette phrase, le
               * menu à une seule entrée passe pour une liste qui n'a pas fini
               * de charger.
               */
              `Une seule personne administre la famille pour l’instant : ${seulCandidat.nomAffiche}. ` +
              'C’est elle qui suivra ce projet, et elle le verra.'}
        </span>

        <select
          id="referent-du-projet"
          name="referent-du-projet"
          aria-describedby="aide-du-referent"
          value={choisi}
          onChange={(evenement) => surChoix(evenement.target.value)}
        >
          {/*
           * L'entrée vide n'existe QUE s'il y a un vrai choix à faire. Avec un
           * seul candidat, elle n'offrirait qu'une façon de se tromper.
           */}
          {seulCandidat === undefined && <option value="">— choisissez une personne —</option>}

          {candidats.map((candidat) => (
            <option key={candidat.compteId} value={candidat.compteId}>
              {candidat.nomAffiche}
            </option>
          ))}
        </select>
      </p>

      <p className="champ" data-role="niveau-du-referent">
        <label htmlFor="niveau-du-referent">Ce que le référent pourra faire</label>
        <select
          id="niveau-du-referent"
          name="niveau-du-referent"
          value={niveau}
          onChange={(evenement) => surNiveau(evenement.target.value as NiveauDeMembre)}
        >
          <option value="lecture">{CE_QUE_LE_NIVEAU_DONNE.lecture}</option>
          <option value="modification">{CE_QUE_LE_NIVEAU_DONNE.modification}</option>
        </select>
      </p>
    </>
  );
}
